/**
 * @module core/api/api-client
 * @description Base API client for all backend communications.
 * Provides centralized configuration, error handling, and request/response interceptors.
 */

import { fetchWithAuth } from '../auth/token-manager.js';
import { logger } from '../../utils/logger.js';

/**
 * Azure configuration - dynamically imported
 * @type {Object}
 */
let azureConfig = null;

/**
 * Initialize API client with Azure configuration
 * This should be called during application startup
 *
 * @param {Object} config - Azure configuration object
 */
export function initializeApiClient(config) {
    if (!config || !config.baseUrl) {
        throw new Error('Invalid Azure configuration. baseUrl is required.');
    }
    azureConfig = config;
    logger.info('✅ API Client initialized with base URL:', config.baseUrl);
}

/**
 * Get the configured base URL
 *
 * @returns {string} Base URL for API calls
 * @throws {Error} If API client not initialized
 */
export function getBaseUrl() {
    if (!azureConfig) {
        throw new Error('API Client not initialized. Call initializeApiClient first.');
    }
    return azureConfig.baseUrl;
}

/**
 * Make an authenticated GET request
 *
 * @param {string} endpoint - API endpoint (relative to base URL)
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} If request fails
 *
 * @example
 * const response = await get('/api/projects/12345/files');
 * const data = await response.json();
 */
export async function get(endpoint, options = {}) {
    const url = `${getBaseUrl()}${endpoint}`;
    return await fetchWithAuth(url, {
        method: 'GET',
        ...options
    });
}

/**
 * Make an authenticated POST request
 *
 * @param {string} endpoint - API endpoint (relative to base URL)
 * @param {Object|FormData} body - Request body (will be JSON stringified if object)
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} If request fails
 *
 * @example
 * const response = await post('/api/projects/12345/files', formData);
 * const result = await response.json();
 */
export async function post(endpoint, body = null, options = {}) {
    const url = `${getBaseUrl()}${endpoint}`;

    // Handle FormData differently - don't stringify or set Content-Type
    const isFormData = body instanceof FormData;

    const fetchOptions = {
        method: 'POST',
        ...options
    };

    if (body) {
        if (isFormData) {
            // For FormData, let the browser set the Content-Type with boundary
            fetchOptions.body = body;
            // Remove Content-Type header if present to allow browser to set it
            if (fetchOptions.headers && fetchOptions.headers['Content-Type']) {
                delete fetchOptions.headers['Content-Type'];
            }
        } else {
            // For JSON data
            fetchOptions.body = JSON.stringify(body);
        }
    }

    return await fetchWithAuth(url, fetchOptions);
}

/**
 * Make an authenticated DELETE request
 *
 * @param {string} endpoint - API endpoint (relative to base URL)
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} If request fails
 *
 * @example
 * const response = await del('/api/projects/12345/files/67890');
 * const result = await response.json();
 */
export async function del(endpoint, options = {}) {
    const url = `${getBaseUrl()}${endpoint}`;
    return await fetchWithAuth(url, {
        method: 'DELETE',
        ...options
    });
}

/**
 * Make an authenticated PUT request
 *
 * @param {string} endpoint - API endpoint (relative to base URL)
 * @param {Object} body - Request body (will be JSON stringified)
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} If request fails
 *
 * @example
 * const response = await put('/api/projects/12345', { name: 'Updated' });
 * const result = await response.json();
 */
export async function put(endpoint, body = null, options = {}) {
    const url = `${getBaseUrl()}${endpoint}`;
    return await fetchWithAuth(url, {
        method: 'PUT',
        body: body ? JSON.stringify(body) : null,
        ...options
    });
}

/**
 * Handle API response and extract JSON data
 * Provides consistent error handling for API responses
 *
 * @param {Response} response - Fetch response
 * @param {string} errorMessage - Custom error message prefix
 * @returns {Promise<Object>} Parsed JSON response
 * @throws {Error} If response is not OK or parsing fails
 *
 * @example
 * const response = await get('/api/projects');
 * const data = await handleResponse(response, 'Failed to load projects');
 */
export async function handleResponse(response, errorMessage = 'API request failed') {
    if (!response.ok) {
        let errorDetails = '';

        try {
            const errorData = await response.json();
            if (errorData.Details) {
                errorDetails = errorData.Details;
            } else if (errorData.Error) {
                errorDetails = errorData.Error;
            } else if (errorData.error) {
                errorDetails = errorData.error;
            } else if (errorData.message) {
                errorDetails = errorData.message;
            }
        } catch {
            // Fallback to status text if JSON parsing fails
            errorDetails = response.statusText;
        }

        const fullError = errorDetails
            ? `${errorMessage}: ${errorDetails}`
            : `${errorMessage}: ${response.status} ${response.statusText}`;

        throw new Error(fullError);
    }

    return await response.json();
}

/**
 * Strip entity prefix from file ID if present
 * The API expects numeric IDs, not prefixed versions
 *
 * @param {string} fileId - File ID (may include entity prefix like "project_12345")
 * @returns {string} Clean file ID without prefix
 *
 * @example
 * stripEntityPrefix('project_12345') // Returns '12345'
 * stripEntityPrefix('12345') // Returns '12345'
 */
export function stripEntityPrefix(fileId) {
    if (!fileId) return fileId;

    const prefixPattern = /^(project|opportunity|lead|contact)_/;
    if (prefixPattern.test(fileId)) {
        const cleanId = fileId.replace(prefixPattern, '');
        logger.debug(`🔧 Stripped entity prefix: ${fileId} -> ${cleanId}`);
        return cleanId;
    }

    return fileId;
}

/**
 * Build query string from parameters object
 *
 * @param {Object} params - Query parameters
 * @returns {string} Query string (including leading ?)
 *
 * @example
 * buildQueryString({ q: 'search term', limit: 10 })
 * // Returns '?q=search%20term&limit=10'
 */
export function buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) {
        return '';
    }

    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
            queryParams.append(key, value);
        }
    });

    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
}
